import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { getAdherentsByGroup } from "../../../../../services/dashboardServices";
import { getDojos } from "../../../../../services/dojoServices";
import { getGroupsList } from "../../../../../services/groupServices";

import {
  FaChartBar,
  FaChalkboardTeacher,
  FaFilter,
  FaRedo,
  FaUsers,
  FaUserCheck,
} from "react-icons/fa";

function normalizeName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function getPercent(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / total) * 100);
}

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.groups)) return data.groups;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function isActiveGroup(group) {
  const value = group?.is_active;

  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;

  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return ["true", "1", "yes", "on"].includes(v);
  }

  return !!value;
}

function DashboardMiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
    cyan:
      "border-cyan-200 bg-cyan-50 text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-black text-slate-950 dark:text-white">
            {value}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload || {};
  const used =
    payload.find((item) => item.dataKey === "adherentCount")?.value ?? 0;
  const rest =
    payload.find((item) => item.dataKey === "remainingQuota")?.value ?? 0;
  const quota = Number(row.quota || used + rest || 0);
  const fillRate = quota ? getPercent(used, quota) : 0;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-black text-slate-900 dark:text-white">{label}</p>

      {row.dojoNom ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Dojo : <span className="font-semibold">{row.dojoNom}</span>
        </p>
      ) : null}

      <div className="mt-3 space-y-1 text-slate-700 dark:text-slate-300">
        <p>
          Inscrits : <span className="font-black">{formatNumber(used)}</span>
        </p>
        <p>
          Quota restant :{" "}
          <span className="font-black">{formatNumber(rest)}</span>
        </p>
        <p>
          Capacité : <span className="font-black">{formatNumber(quota)}</span>
        </p>
      </div>

      <p className="mt-2 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        Remplissage : {fillRate}%
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="h-5 w-64 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="h-[340px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

function EmptyState({ selectedDojoName }) {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaChalkboardTeacher />
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-900 dark:text-white">
        Aucun groupe actif à afficher
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        {selectedDojoName
          ? `Aucun groupe actif trouvé pour le dojo ${selectedDojoName}.`
          : "Aucun groupe actif n’a été trouvé pour le moment."}
      </p>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-black">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-black text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

export default function GroupDistributionChart() {
  const [rawCounts, setRawCounts] = useState([]);
  const [dojos, setDojos] = useState([]);
  const [groups, setGroups] = useState([]);

  const [selectedDojo, setSelectedDojo] = useState("");
  const [sortMode, setSortMode] = useState("dojo");
  const [displayMode, setDisplayMode] = useState("all");

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const [dojoList, counts, groupsList] = await Promise.all([
        getDojos(),
        getAdherentsByGroup(),
        getGroupsList(),
      ]);

      setDojos(extractArray(dojoList));
      setRawCounts(extractArray(counts));
      setGroups(extractArray(groupsList));
    } catch (error) {
      console.error("Erreur chargement dojos/comptages/groupes :", error);
      setDojos([]);
      setRawCounts([]);
      setGroups([]);
      setErrorMsg("Impossible de charger la répartition des adhérents par cours.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const dojoById = useMemo(() => {
    const map = new Map();

    dojos.forEach((dojo) => {
      map.set(String(dojo.id), dojo);
    });

    return map;
  }, [dojos]);

  const countsByName = useMemo(() => {
    const map = new Map();

    rawCounts.forEach((count) => {
      const key = normalizeName(count.nom);

      if (!key) return;

      map.set(key, {
        adherentCount: Number(count.adherentCount || 0),
        quota_groupe: Number(count.quota_groupe || 0),
        ordre_groupe: Number(count.ordre_groupe || 9999),
        nom: count.nom,
      });
    });

    return map;
  }, [rawCounts]);

  const merged = useMemo(() => {
    const rows = groups
      .filter(isActiveGroup)
      .map((group) => {
        const key = normalizeName(group.nom);
        const count = countsByName.get(key);

        const adherentCount = Number(count?.adherentCount || 0);
        const quota = Math.max(
          0,
          Number(group?.quota_groupe ?? count?.quota_groupe ?? 0)
        );
        const remainingQuota = Math.max(0, quota - adherentCount);
        const fillRate = quota ? getPercent(adherentCount, quota) : 0;

        const ordre = Number(group?.ordre_groupe ?? count?.ordre_groupe ?? 9999);
        const dojoId = String(group?.dojo_id ?? "");
        const dojoNom = dojoById.get(dojoId)?.dojo_nom || "Sans dojo";

        return {
          id: Number(group.id),
          nom: group.nom || count?.nom || "Sans nom",
          adherentCount,
          remainingQuota,
          quota,
          fillRate,
          ordre,
          dojoId,
          dojoNom,
        };
      });

    if (sortMode === "fill-desc") {
      return rows.sort((a, b) => b.fillRate - a.fillRate || b.adherentCount - a.adherentCount);
    }

    if (sortMode === "count-desc") {
      return rows.sort((a, b) => b.adherentCount - a.adherentCount);
    }

    if (sortMode === "name") {
      return rows.sort((a, b) => String(a.nom).localeCompare(String(b.nom), "fr"));
    }

    return rows.sort((a, b) => {
      if ((a.dojoNom || "") !== (b.dojoNom || "")) {
        return String(a.dojoNom || "").localeCompare(String(b.dojoNom || ""), "fr");
      }

      if (a.ordre !== b.ordre) return a.ordre - b.ordre;

      return String(a.nom).localeCompare(String(b.nom), "fr");
    });
  }, [groups, countsByName, dojoById, sortMode]);

  const filtered = useMemo(() => {
    let rows = merged;

    if (selectedDojo) {
      rows = rows.filter((row) => String(row.dojoId) === String(selectedDojo));
    }

    if (displayMode === "top10") {
      return [...rows]
        .sort((a, b) => b.adherentCount - a.adherentCount)
        .slice(0, 10);
    }

    if (displayMode === "almost-full") {
      return rows.filter((row) => row.quota > 0 && row.fillRate >= 80);
    }

    if (displayMode === "available") {
      return rows.filter((row) => row.quota > 0 && row.remainingQuota > 0);
    }

    return rows;
  }, [merged, selectedDojo, displayMode]);

  const selectedDojoName = dojoById.get(String(selectedDojo))?.dojo_nom || "";

  const kpis = useMemo(() => {
    const groupsCount = filtered.length;
    const adherentsCount = filtered.reduce(
      (sum, row) => sum + Number(row.adherentCount || 0),
      0
    );
    const totalQuota = filtered.reduce(
      (sum, row) => sum + Number(row.quota || 0),
      0
    );
    const remaining = filtered.reduce(
      (sum, row) => sum + Number(row.remainingQuota || 0),
      0
    );
    const globalFillRate = totalQuota ? getPercent(adherentsCount, totalQuota) : 0;

    return {
      groupsCount,
      adherentsCount,
      totalQuota,
      remaining,
      globalFillRate,
    };
  }, [filtered]);

  const chartHeight = Math.min(
    720,
    Math.max(320, filtered.length * 42 + 90)
  );

  if (loading) return <LoadingSkeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaChalkboardTeacher />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Répartition des adhérents par cours
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Suivi des inscrits par groupe actif, avec quota restant et taux
                de remplissage.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaRedo className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardMiniStat
          icon={<FaChalkboardTeacher />}
          label="Groupes Actifs"
          value={formatNumber(kpis.groupsCount)}
          helper={selectedDojoName || "Tous les dojos"}
          accent="indigo"
        />

        <DashboardMiniStat
          icon={<FaUsers />}
          label="Inscrits sur ces groupes"
          value={formatNumber(kpis.adherentsCount)}
          helper="Total sur les groupes affichés"
          accent="emerald"
        />

        <DashboardMiniStat
          icon={<FaUserCheck />}
          label="Places restantes"
          value={formatNumber(kpis.remaining)}
          helper={`Capacité totale : ${formatNumber(kpis.totalQuota)}`}
          accent="amber"
        />

        <DashboardMiniStat
          icon={<FaChartBar />}
          label="Remplissage"
          value={`${kpis.globalFillRate}%`}
          helper="Taux global sur la sélection"
          accent="cyan"
        />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              <FaFilter />
              Filtres
            </div>

            <div className="flex max-w-full gap-2 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => setSelectedDojo("")}
                className={`whitespace-nowrap rounded-2xl px-3 py-2 text-sm font-bold transition ${
                  !selectedDojo
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                }`}
              >
                Tous
              </button>

              {dojos.map((dojo) => (
                <button
                  key={dojo.id}
                  type="button"
                  onClick={() => setSelectedDojo(String(dojo.id))}
                  className={`whitespace-nowrap rounded-2xl px-3 py-2 text-sm font-bold transition ${
                    String(selectedDojo) === String(dojo.id)
                      ? "bg-indigo-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                  }`}
                >
                  {dojo.dojo_nom || `Dojo ${dojo.id}`}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:min-w-[460px]">
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Affichage
              </label>
              <select
                value={displayMode}
                onChange={(event) => setDisplayMode(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="all">Tous les groupes</option>
                <option value="top10">Top 10 inscrits</option>
                <option value="almost-full">Groupes presque pleins</option>
                <option value="available">Groupes avec places</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
                Tri
              </label>
              <select
                value={sortMode}
                onChange={(event) => setSortMode(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <option value="dojo">Dojo puis ordre</option>
                <option value="count-desc">Plus d’inscrits</option>
                <option value="fill-desc">Taux de remplissage</option>
                <option value="name">Nom du groupe</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {!filtered.length ? (
        <EmptyState selectedDojoName={selectedDojoName} />
      ) : (
        <div className="rounded-[24px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60">
          <div className="max-h-[760px] overflow-y-auto pr-1">
            <div style={{ height: chartHeight, minWidth: 720 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={filtered}
                  margin={{ top: 12, right: 48, left: 8, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(100,116,139,0.25)"
                  />

                  <XAxis
                    type="number"
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#64748b" }}
                  />

                  <YAxis
                    type="category"
                    dataKey="nom"
                    width={220}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#64748b" }}
                  />

                  <Tooltip content={<ChartTooltip />} />

                  <Legend
                    wrapperStyle={{
                      fontSize: 12,
                      color: "#64748b",
                    }}
                  />

                  <Bar
                    dataKey="adherentCount"
                    stackId="quota"
                    name="Inscrits"
                    fill="#6366F1"
                    radius={[8, 0, 0, 8]}
                  />

                  <Bar
                    dataKey="remainingQuota"
                    stackId="quota"
                    name="Quota restant"
                    fill="#34D399"
                    radius={[0, 8, 8, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
            Les barres représentent le quota total du groupe : inscrits + places
            restantes. Les groupes sans quota défini affichent uniquement le
            nombre d’inscrits.
          </p>
        </div>
      )}
    </div>
  );
}